/**
 * Treasured Scents - Follow Cursor Effect
 * Replicates the Next.js cursor following effect with ring and trailing dot
 * Based on follow-cursor-new.tsx from the Next.js implementation
 */

class TreasuredScentsFollowCursor {
  constructor(options = {}) {
    // Default configuration using theme primary color
    this.config = {
      color: options.color || this.getPrimaryColor(), // Use theme primary color
      size: options.size || 20, // Ring size (larger = more premium)
      lag: options.lag || 8, // Ring lag for smooth, fluid movement
      dotSize: options.dotSize || 3, // Trailing dot size
      dotLag: options.dotLag || 3, // Dot follows closely (crisp center)
      zIndex: options.zIndex || 9000, // Below chat widgets
      hoverScale: options.hoverScale || 2, // Scale factor on hover
      hoverElements: options.hoverElements || [
        ".product-card",
        ".card",
        ".collection-card",
        ".button",
        "button",
        "a[href]",
      ], // Elements that trigger hover effect (Tinker DOM)
    };

    // Hover state
    this.isHovering = false;
    this.hoverScale = 1;

    // Canvas and animation properties
    this.canvas = null;
    this.context = null;
    this.animationFrame = null;
    this.width = window.innerWidth;
    this.height = window.innerHeight;

    // Cursor tracking
    this.cursor = { x: this.width / 2, y: this.height / 2 };

    // Pulse animation
    this.pulseSize = 0;
    this.pulseDirection = 1;

    // Accessibility check
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    // Initialize if motion is allowed
    this.init();
  }

  /**
   * Get the primary color from CSS custom properties
   */
  getPrimaryColor() {
    // Try to get the primary color from CSS custom properties
    const computedStyle = getComputedStyle(document.documentElement);

    // Try different possible CSS custom property names for primary color.
    // Tinker exposes --tank-accent (pink) and --color-foreground (black); prefer the pink accent.
    const primaryColorVars = [
      "--tank-accent",
      "--color-primary-button-background",
      "--primary",
      "--color-primary",
      "--color-foreground",
      "--accent",
      "--brand-color",
    ];

    for (const varName of primaryColorVars) {
      const color = computedStyle.getPropertyValue(varName).trim();
      if (color) {
        // If it's a hex color, convert to rgba
        if (color.startsWith("#")) {
          return this.hexToRgba(color);
        }
        // If it's an HSL value, convert to rgba
        if (color.startsWith("hsl")) {
          return this.hslToRgba(color);
        }
        // If it's already rgba or rgb, use it
        if (color.startsWith("rgba") || color.startsWith("rgb")) {
          return color;
        }
        // If it's just numbers (like "200 64 233"), assume it's RGB values
        if (/^\d+\s+\d+\s+\d+$/.test(color)) {
          return `rgba(${color}, 1)`;
        }
      }
    }

    // Fallback to theme's default primary color
    return "rgba(210, 104, 111, 1)"; // #d2686f converted to rgba
  }

  /**
   * Convert hex color to RGBA
   */
  hexToRgba(hex) {
    // Remove # if present
    hex = hex.replace("#", "");

    // Handle 3-digit hex
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    // Handle 6-digit hex
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, 1)`;
    }

    // Fallback
    return "rgba(210, 104, 111, 1)";
  }

  /**
   * Convert HSL to RGBA
   */
  hslToRgba(hslString) {
    // Extract HSL values from string like "hsl(280, 70%, 60%)"
    const hslMatch = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (!hslMatch) return "rgba(210, 104, 111, 1)";

    const h = parseInt(hslMatch[1]) / 360;
    const s = parseInt(hslMatch[2]) / 100;
    const l = parseInt(hslMatch[3]) / 100;

    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
    const g = Math.round(hue2rgb(p, q, h) * 255);
    const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

    return `rgba(${r}, ${g}, ${b}, 1)`;
  }

  /**
   * Ring class - follows mouse cursor with lag
   */
  createRing(x, y, width, lag) {
    return {
      position: { x, y },
      width,
      lag,
      moveTowards: (targetX, targetY, context) => {
        // Smooth movement with lag
        this.ring.position.x +=
          (targetX - this.ring.position.x) / this.ring.lag;
        this.ring.position.y +=
          (targetY - this.ring.position.y) / this.ring.lag;

        // Add subtle shadow using primary color
        context.shadowColor = this.config.color;
        context.shadowBlur = 4;

        // Draw ring (border only, no fill)
        context.strokeStyle = this.config.color;
        context.lineWidth = 1;
        context.beginPath();
        context.arc(
          this.ring.position.x,
          this.ring.position.y,
          this.ring.width,
          0,
          2 * Math.PI
        );
        context.stroke();
        context.closePath();

        // Reset shadow
        context.shadowBlur = 0;
      },
    };
  }

  /**
   * Trailing Dot class - follows ring with additional lag
   */
  createTrailingDot(x, y, width, lag) {
    return {
      position: { x, y },
      width,
      lag,
      moveTowards: (ringX, ringY, ringWidth, context) => {
        // Position at bottom-left of ring
        const targetX = ringX - ringWidth * 0.7;
        const targetY = ringY + ringWidth * 0.7;

        this.trailingDot.position.x +=
          (targetX - this.trailingDot.position.x) / this.trailingDot.lag;
        this.trailingDot.position.y +=
          (targetY - this.trailingDot.position.y) / this.trailingDot.lag;

        // Add subtle shadow using primary color with transparency
        const shadowColor = this.config.color.replace(
          /rgba?\(([^)]+)\)/,
          "rgba($1, 0.15)"
        );
        context.shadowColor = shadowColor;
        context.shadowBlur = 3;

        // Draw filled dot
        context.fillStyle = this.config.color;
        context.beginPath();
        context.arc(
          this.trailingDot.position.x,
          this.trailingDot.position.y,
          this.trailingDot.width,
          0,
          2 * Math.PI
        );
        context.fill();
        context.closePath();

        // Reset shadow
        context.shadowBlur = 0;
      },
    };
  }

  /**
   * Mouse move event handler
   */
  onMouseMove = (e) => {
    this.cursor.x = e.clientX;
    this.cursor.y = e.clientY;
  };

  /**
   * Window resize event handler
   */
  onWindowResize = () => {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    if (this.canvas) {
      this.canvas.width = this.width;
      this.canvas.height = this.height;
    }
  };

  /**
   * Setup hover detection for interactive elements
   */
  setupHoverDetection() {
    const elements = document.querySelectorAll(
      this.config.hoverElements.join(", ")
    );

    elements.forEach((element) => {
      element.addEventListener("mouseenter", () => {
        this.isHovering = true;
      });

      element.addEventListener("mouseleave", () => {
        this.isHovering = false;
      });
    });
  }

  /**
   * Update cursor animation
   */
  updateCursor() {
    if (this.context) {
      this.context.clearRect(0, 0, this.width, this.height);

      // Update hover scale with smooth transition
      const targetScale = this.isHovering ? this.config.hoverScale : 1;
      this.hoverScale += (targetScale - this.hoverScale) * 0.1;

      // Update pulse effect (very subtle)
      this.pulseSize += 0.04 * this.pulseDirection;
      if (this.pulseSize > 0.8) this.pulseDirection = -1;
      if (this.pulseSize < 0) this.pulseDirection = 1;

      // Apply pulse and hover scale to ring size
      const pulsedSize = (this.config.size + this.pulseSize) * this.hoverScale;
      this.ring.width = pulsedSize;

      // Apply hover scale to dot size too
      this.trailingDot.width = this.config.dotSize * this.hoverScale;

      // Draw ring first
      this.ring.moveTowards(this.cursor.x, this.cursor.y, this.context);

      // Then draw trailing dot
      this.trailingDot.moveTowards(
        this.ring.position.x,
        this.ring.position.y,
        this.ring.width,
        this.context
      );
    }
  }

  /**
   * Animation loop
   */
  loop = () => {
    this.updateCursor();
    this.animationFrame = requestAnimationFrame(this.loop);
  };

  /**
   * Initialize the cursor effect
   */
  init() {
    // Skip if user prefers reduced motion
    if (this.prefersReducedMotion.matches) {
      console.log("Reduced motion enabled, cursor effect skipped.");
      return;
    }

    // Create canvas
    this.canvas = document.createElement("canvas");
    this.context = this.canvas.getContext("2d");

    // Canvas styling
    this.canvas.style.position = "fixed";
    this.canvas.style.top = "0";
    this.canvas.style.left = "0";
    this.canvas.style.pointerEvents = "none";
    this.canvas.style.zIndex = this.config.zIndex.toString();
    this.canvas.width = this.width;
    this.canvas.height = this.height;

    // Add to DOM
    document.body.appendChild(this.canvas);

    // Create ring and trailing dot instances
    this.ring = this.createRing(
      this.width / 2,
      this.height / 2,
      this.config.size,
      this.config.lag
    );
    this.trailingDot = this.createTrailingDot(
      this.width / 2,
      this.height / 2,
      this.config.dotSize,
      this.config.dotLag
    );

    // Event listeners
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("resize", this.onWindowResize);

    // Setup hover detection for interactive elements
    this.setupHoverDetection();

    // Handle reduced motion preference changes
    this.prefersReducedMotion.onchange = () => {
      if (this.prefersReducedMotion.matches) {
        this.destroy();
      } else {
        this.init();
      }
    };

    // Start animation loop
    this.loop();
  }

  /**
   * Destroy the cursor effect and clean up
   */
  destroy() {
    if (this.canvas) {
      this.canvas.remove();
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("resize", this.onWindowResize);
  }
}

// Auto-initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  // Only initialize on desktop/tablet (not mobile)
  if (window.innerWidth >= 768) {
    window.treasuredScentsFollowCursor = new TreasuredScentsFollowCursor();
  }
});

// Handle window resize to reinitialize on screen size changes
window.addEventListener("resize", () => {
  if (window.treasuredScentsFollowCursor) {
    if (window.innerWidth < 768) {
      // Destroy on mobile
      window.treasuredScentsFollowCursor.destroy();
      window.treasuredScentsFollowCursor = null;
    }
  } else if (window.innerWidth >= 768) {
    // Initialize on desktop/tablet
    window.treasuredScentsFollowCursor = new TreasuredScentsFollowCursor();
  }
});

// Utility function to refresh hover detection (useful for dynamic content)
window.refreshCursorHoverDetection = () => {
  if (window.treasuredScentsFollowCursor) {
    window.treasuredScentsFollowCursor.setupHoverDetection();
  }
};
